/**
 * Marketing Report Builder Modal
 * ===============================
 * Cometly-style report builder for creating custom marketing reports.
 * 
 * Features:
 * - Report name input
 * - Visualization type selector (bar, line, area, pie, table, number)
 * - Metrics picker with searchable list and categories
 * - Date range selector with presets and custom range
 * - Group by configuration (time granularity + dimensions)
 * - Filters panel with multiple filter support
 * - Live preview of report configuration
 * - "Add To Page" button to add report to dashboard
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  BarChart3,
  LineChart,
  AreaChart,
  PieChart,
  Table2,
  Hash,
  Search,
  X,
  Plus,
  Trash2,
  AlertCircle,
  Loader2,
  ChevronRight,
  ChevronDown,
  Calendar,
  Filter,
  Layers,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Visualization type options
const VISUALIZATION_TYPES = [
  { id: 'bar', label: 'Bar', icon: BarChart3 },
  { id: 'line', label: 'Line', icon: LineChart },
  { id: 'area', label: 'Area', icon: AreaChart },
  { id: 'pie', label: 'Pie', icon: PieChart },
  { id: 'table', label: 'Table', icon: Table2 },
  { id: 'number', label: 'Number', icon: Hash },
];

// Date range presets
const DATE_RANGE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_14_days', label: 'Last 14 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'last_90_days', label: 'Last 90 Days' },
  { value: 'this_week', label: 'This Week' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'this_year', label: 'This Year' },
  { value: 'ytd', label: 'Year to Date' },
  { value: 'custom', label: 'Custom Range' },
];

// Time granularity options
const TIME_GRANULARITIES = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' },
];

const ReportBuilderModal = ({
  isOpen,
  onClose,
  onReportCreated,
  editingReport = null,
  targetPage = 'marketing_tracker',
}) => {
  const { user } = useAuth();
  
  // Form state
  const [reportName, setReportName] = useState('');
  const [visualizationType, setVisualizationType] = useState('bar');
  const [metricsBy, setMetricsBy] = useState('contact');
  const [dateRange, setDateRange] = useState({ preset: 'this_month' });
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [sources, setSources] = useState(['all']);
  const [groupBy, setGroupBy] = useState([]);
  const [filters, setFilters] = useState([]);
  
  // Registry data
  const [metricsRegistry, setMetricsRegistry] = useState({ categories: {}, metrics: [] });
  const [dimensionsRegistry, setDimensionsRegistry] = useState({ categories: {}, dimensions: [] });
  const [filtersRegistry, setFiltersRegistry] = useState({ categories: {}, filters: [] });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [metricsSearchQuery, setMetricsSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showMetricsPicker, setShowMetricsPicker] = useState(false);
  
  // Fetch registries on mount
  useEffect(() => {
    if (isOpen) {
      fetchRegistries();
    }
  }, [isOpen]);
  
  // Populate form when editing
  useEffect(() => {
    if (editingReport) {
      setReportName(editingReport.name || '');
      setVisualizationType(editingReport.visualization_type || 'bar');
      setMetricsBy(editingReport.metrics_by || 'contact');
      setDateRange(editingReport.date_range || { preset: 'this_month' });
      setSelectedMetrics(editingReport.metrics || []);
      setSources(editingReport.sources || ['all']);
      setGroupBy(editingReport.group_by || []);
      setFilters(editingReport.filters || []);
    } else {
      // Reset form for new report
      setReportName('');
      setVisualizationType('bar');
      setMetricsBy('contact');
      setDateRange({ preset: 'this_month' });
      setSelectedMetrics([]);
      setSources(['all']);
      setGroupBy([{ dimension: 'time', time_granularity: 'day', display_only_dates_with_data: false }]);
      setFilters([]);
    }
  }, [editingReport, isOpen]);
  
  const fetchRegistries = async () => {
    setLoading(true);
    try {
      const [metricsRes, dimensionsRes, filtersRes] = await Promise.all([
        axios.get(`${API}/api/marketing/reports/metrics`),
        axios.get(`${API}/api/marketing/reports/dimensions`),
        axios.get(`${API}/api/marketing/reports/filters-registry`),
      ]);
      
      setMetricsRegistry(metricsRes.data);
      setDimensionsRegistry(dimensionsRes.data);
      setFiltersRegistry(filtersRes.data);
      
      // Expand all categories by default
      const expanded = {};
      Object.keys(metricsRes.data.categories || {}).forEach(cat => {
        expanded[cat] = true;
      });
      setExpandedCategories(expanded);
    } catch (error) {
      console.error('Error fetching registries:', error);
      toast.error('Failed to load report builder options');
    } finally {
      setLoading(false);
    }
  };
  
  // Filter metrics by search query
  const filteredMetrics = useMemo(() => {
    if (!metricsSearchQuery.trim()) {
      return metricsRegistry.metrics;
    }
    const query = metricsSearchQuery.toLowerCase();
    return metricsRegistry.metrics.filter(m => 
      m.label.toLowerCase().includes(query) ||
      m.id.toLowerCase().includes(query) ||
      (m.description || '').toLowerCase().includes(query)
    );
  }, [metricsRegistry.metrics, metricsSearchQuery]);
  
  // Group filtered metrics by category
  const metricsByCategory = useMemo(() => {
    const grouped = {};
    filteredMetrics.forEach(metric => {
      const cat = metric.category || 'other';
      if (!grouped[cat]) {
        grouped[cat] = [];
      }
      grouped[cat].push(metric);
    });
    return grouped;
  }, [filteredMetrics]);
  
  // Toggle metric selection
  const toggleMetric = (metricId, metric) => {
    if (!metric.available) return;
    
    setSelectedMetrics(prev => {
      if (prev.includes(metricId)) {
        return prev.filter(m => m !== metricId);
      }
      return [...prev, metricId];
    });
  };
  
  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };
  
  // Add a group by
  const addGroupBy = () => {
    setGroupBy(prev => [...prev, { dimension: 'source' }]);
  };
  
  // Remove a group by
  const removeGroupBy = (index) => {
    setGroupBy(prev => prev.filter((_, i) => i !== index));
  };
  
  // Update a group by
  const updateGroupBy = (index, updates) => {
    setGroupBy(prev => prev.map((gb, i) => i === index ? { ...gb, ...updates } : gb));
  };
  
  // Add a filter
  const addFilter = () => {
    setFilters(prev => [...prev, { field: 'configured_name', operator: 'equals', value: '' }]);
  };
  
  // Remove a filter
  const removeFilter = (index) => {
    setFilters(prev => prev.filter((_, i) => i !== index));
  };
  
  // Update a filter
  const updateFilter = (index, updates) => {
    setFilters(prev => prev.map((f, i) => i === index ? { ...f, ...updates } : f));
  };
  
  // Save report
  const handleSave = async (addToPage = false) => {
    if (!reportName.trim()) {
      toast.error('Please enter a report name');
      return;
    }
    
    if (selectedMetrics.length === 0) {
      toast.error('Please select at least one metric');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        name: reportName.trim(),
        visualization_type: visualizationType,
        metrics_by: metricsBy,
        date_range: dateRange,
        metrics: selectedMetrics,
        sources,
        group_by: groupBy,
        filters: filters.filter(f => f.field && (f.value || f.values?.length)),
      };
      
      let report;
      if (editingReport) {
        const response = await axios.put(
          `${API}/api/marketing/reports/${editingReport.id}`,
          payload
        );
        report = response.data;
        toast.success('Report updated successfully');
      } else {
        const response = await axios.post(`${API}/api/marketing/reports`, payload);
        report = response.data;
        toast.success('Report created successfully');
      }
      
      if (addToPage) {
        // Add widget to dashboard
        await axios.post(
          `${API}/api/marketing/dashboard/add-widget?page=${targetPage}`,
          {
            id: crypto.randomUUID(),
            report_id: report.id,
            x: 0,
            y: 0,
            w: visualizationType === 'number' ? 3 : 6,
            h: visualizationType === 'number' ? 2 : 4,
          }
        );
        toast.success('Report added to page');
      }
      
      onReportCreated?.(report, addToPage);
      onClose();
    } catch (error) {
      console.error('Error saving report:', error);
      toast.error(error.response?.data?.detail || 'Failed to save report');
    } finally {
      setSaving(false);
    }
  };
  
  // Get selected metric details
  const getMetricById = (id) => {
    return metricsRegistry.metrics.find(m => m.id === id);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl">
              {editingReport ? 'Edit Report' : 'Report Builder'}
            </DialogTitle>
            <Button
              onClick={() => handleSave(true)}
              disabled={saving || selectedMetrics.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Add To Page
            </Button>
          </div>
        </DialogHeader>
        
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-6 h-full">
              {/* Left Panel - Configuration */}
              <ScrollArea className="h-[calc(90vh-120px)] pr-4">
                <div className="space-y-6">
                  {/* Report Name */}
                  <div className="space-y-2">
                    <Label htmlFor="report-name">Report Name</Label>
                    <Input
                      id="report-name"
                      value={reportName}
                      onChange={(e) => setReportName(e.target.value)}
                      placeholder="Enter report name..."
                      className="h-10"
                    />
                  </div>
                  
                  {/* Visualization Type */}
                  <div className="space-y-2">
                    <Label>Visualization</Label>
                    <div className="flex gap-2 flex-wrap">
                      {VISUALIZATION_TYPES.map(viz => {
                        const Icon = viz.icon;
                        return (
                          <Button
                            key={viz.id}
                            variant={visualizationType === viz.id ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setVisualizationType(viz.id)}
                            className="flex items-center gap-1.5"
                          >
                            <Icon className="h-4 w-4" />
                            {viz.label}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Report Metrics By */}
                  <div className="space-y-2">
                    <Label>Report Metrics By</Label>
                    <Select value={metricsBy} onValueChange={setMetricsBy}>
                      <SelectTrigger className="h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="contact">Contact</SelectItem>
                        <SelectItem value="company">Company</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Date Range */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Date Range
                    </Label>
                    <Select 
                      value={dateRange.preset} 
                      onValueChange={(v) => setDateRange({ ...dateRange, preset: v })}
                    >
                      <SelectTrigger className="h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DATE_RANGE_PRESETS.map(preset => (
                          <SelectItem key={preset.value} value={preset.value}>
                            {preset.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {dateRange.preset === 'custom' && (
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div>
                          <Label className="text-xs">Start Date</Label>
                          <Input
                            type="date"
                            value={dateRange.custom_start || ''}
                            onChange={(e) => setDateRange({ ...dateRange, custom_start: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">End Date</Label>
                          <Input
                            type="date"
                            value={dateRange.custom_end || ''}
                            onChange={(e) => setDateRange({ ...dateRange, custom_end: e.target.value })}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <Separator />
                  
                  {/* Metrics Picker */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="flex items-center gap-2">
                        <Layers className="h-4 w-4" />
                        Metrics
                        {selectedMetrics.length > 0 && (
                          <Badge variant="secondary" className="ml-2">
                            {selectedMetrics.length} selected
                          </Badge>
                        )}
                      </Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowMetricsPicker(!showMetricsPicker)}
                      >
                        {showMetricsPicker ? 'Hide' : 'Show'} Picker
                      </Button>
                    </div>
                    
                    {/* Selected Metrics Display */}
                    {selectedMetrics.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 p-2 bg-muted/50 rounded-lg">
                        {selectedMetrics.map(metricId => {
                          const metric = getMetricById(metricId);
                          return (
                            <Badge
                              key={metricId}
                              variant="secondary"
                              className="flex items-center gap-1 pr-1"
                            >
                              {metric?.label || metricId}
                              {metric?.source === 'computed_from_cometly' && (
                                <span className="text-[10px] text-muted-foreground ml-1">
                                  (Computed)
                                </span>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-4 w-4 p-0 ml-1 hover:bg-destructive/20"
                                onClick={() => toggleMetric(metricId, metric)}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Metrics Picker Panel */}
                    {showMetricsPicker && (
                      <div className="border rounded-lg p-3 space-y-3 bg-card">
                        {/* Search */}
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Search metrics..."
                            value={metricsSearchQuery}
                            onChange={(e) => setMetricsSearchQuery(e.target.value)}
                            className="pl-9 h-9"
                          />
                        </div>
                        
                        {/* Categories and Metrics */}
                        <ScrollArea className="h-[300px]">
                          <div className="space-y-2">
                            {Object.entries(metricsByCategory).map(([categoryId, categoryMetrics]) => (
                              <div key={categoryId} className="space-y-1">
                                <button
                                  onClick={() => toggleCategory(categoryId)}
                                  className="flex items-center gap-2 w-full text-left text-sm font-medium hover:text-primary"
                                >
                                  {expandedCategories[categoryId] ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4" />
                                  )}
                                  {metricsRegistry.categories[categoryId] || categoryId}
                                  <Badge variant="outline" className="ml-auto text-[10px]">
                                    {categoryMetrics.length}
                                  </Badge>
                                </button>
                                
                                {expandedCategories[categoryId] && (
                                  <div className="ml-6 space-y-1">
                                    {categoryMetrics.map(metric => (
                                      <TooltipProvider key={metric.id}>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <label
                                              className={`flex items-center gap-2 p-1.5 rounded hover:bg-muted/50 cursor-pointer ${
                                                !metric.available ? 'opacity-50 cursor-not-allowed' : ''
                                              }`}
                                            >
                                              <Checkbox
                                                checked={selectedMetrics.includes(metric.id)}
                                                onCheckedChange={() => toggleMetric(metric.id, metric)}
                                                disabled={!metric.available}
                                              />
                                              <span className="text-sm flex-1">
                                                {metric.label}
                                              </span>
                                              {metric.source === 'computed_from_cometly' && (
                                                <Badge variant="outline" className="text-[9px] h-4">
                                                  Computed
                                                </Badge>
                                              )}
                                              {!metric.available && (
                                                <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                                              )}
                                            </label>
                                          </TooltipTrigger>
                                          <TooltipContent side="right" className="max-w-xs">
                                            <p className="font-medium">{metric.label}</p>
                                            {metric.description && (
                                              <p className="text-xs text-muted-foreground mt-1">
                                                {metric.description}
                                              </p>
                                            )}
                                            {!metric.available && metric.unavailable_reason && (
                                              <p className="text-xs text-amber-500 mt-1">
                                                ⚠️ {metric.unavailable_reason}
                                              </p>
                                            )}
                                            <p className="text-[10px] text-muted-foreground mt-1">
                                              Source: {metric.source}
                                            </p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </div>
                    )}
                  </div>
                  
                  <Separator />
                  
                  {/* Group By */}
                  <div className="space-y-3">
                    <Label className="flex items-center gap-2">
                      <Layers className="h-4 w-4" />
                      Group By
                    </Label>
                    
                    {groupBy.map((gb, index) => (
                      <div key={index} className="flex items-start gap-2 p-3 border rounded-lg bg-muted/30">
                        <div className="flex-1 space-y-2">
                          {gb.dimension === 'time' ? (
                            <>
                              <div className="flex items-center gap-2">
                                <Label className="text-xs w-24">Time:</Label>
                                <div className="flex gap-1">
                                  {TIME_GRANULARITIES.map(tg => (
                                    <Button
                                      key={tg.value}
                                      variant={gb.time_granularity === tg.value ? 'default' : 'outline'}
                                      size="sm"
                                      className="h-7 text-xs"
                                      onClick={() => updateGroupBy(index, { time_granularity: tg.value })}
                                    >
                                      {tg.label}
                                    </Button>
                                  ))}
                                </div>
                              </div>
                              <label className="flex items-center gap-2 text-xs">
                                <Checkbox
                                  checked={gb.display_only_dates_with_data || false}
                                  onCheckedChange={(checked) => 
                                    updateGroupBy(index, { display_only_dates_with_data: checked })
                                  }
                                />
                                Display only dates with data
                              </label>
                            </>
                          ) : (
                            <Select
                              value={gb.dimension}
                              onValueChange={(v) => updateGroupBy(index, { dimension: v })}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder="Select dimension" />
                              </SelectTrigger>
                              <SelectContent>
                                {dimensionsRegistry.dimensions
                                  .filter(d => d.id !== 'time')
                                  .map(dim => (
                                    <SelectItem key={dim.id} value={dim.id}>
                                      {dim.label}
                                    </SelectItem>
                                  ))
                                }
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        
                        {groupBy.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                            onClick={() => removeGroupBy(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addGroupBy}
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Another Group By
                    </Button>
                  </div>
                  
                  <Separator />
                  
                  {/* Filters */}
                  <div className="space-y-3">
                    <Label className="flex items-center gap-2">
                      <Filter className="h-4 w-4" />
                      Filters
                      {filters.length > 0 && (
                        <Badge variant="secondary">{filters.length}</Badge>
                      )}
                    </Label>
                    
                    {filters.map((filter, index) => (
                      <div key={index} className="flex items-start gap-2 p-3 border rounded-lg bg-muted/30">
                        <div className="flex-1 grid grid-cols-3 gap-2">
                          <Select
                            value={filter.field}
                            onValueChange={(v) => updateFilter(index, { field: v })}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Field" />
                            </SelectTrigger>
                            <SelectContent>
                              {filtersRegistry.filters.map(f => (
                                <SelectItem key={f.id} value={f.field}>
                                  {f.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          
                          <Select
                            value={filter.operator}
                            onValueChange={(v) => updateFilter(index, { operator: v })}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Operator" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="equals">Equals</SelectItem>
                              <SelectItem value="not_equals">Not Equals</SelectItem>
                              <SelectItem value="contains">Contains</SelectItem>
                              <SelectItem value="in">In</SelectItem>
                              <SelectItem value="not_in">Not In</SelectItem>
                            </SelectContent>
                          </Select>
                          
                          <Input
                            value={filter.value || ''}
                            onChange={(e) => updateFilter(index, { value: e.target.value })}
                            placeholder="Value"
                            className="h-9"
                          />
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                          onClick={() => removeFilter(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addFilter}
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Filter
                    </Button>
                  </div>
                </div>
              </ScrollArea>
              
              {/* Right Panel - Preview */}
              <div className="border-l pl-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-lg font-semibold">Preview</Label>
                    <Badge variant="outline">
                      {visualizationType.charAt(0).toUpperCase() + visualizationType.slice(1)}
                    </Badge>
                  </div>
                  
                  <div className="border rounded-lg p-4 bg-muted/20 min-h-[300px] flex flex-col">
                    {selectedMetrics.length === 0 ? (
                      <div className="flex-1 flex items-center justify-center text-muted-foreground">
                        <div className="text-center">
                          <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-30" />
                          <p>Select metrics to preview</p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="text-sm font-medium">
                          {reportName || 'Untitled Report'}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {DATE_RANGE_PRESETS.find(p => p.value === dateRange.preset)?.label || 'Custom Range'}
                        </div>
                        <Separator />
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-muted-foreground">Selected Metrics:</p>
                          <div className="flex flex-wrap gap-1.5">
                            {selectedMetrics.map(metricId => {
                              const metric = getMetricById(metricId);
                              return (
                                <Badge key={metricId} variant="secondary" className="text-xs">
                                  {metric?.label || metricId}
                                </Badge>
                              );
                            })}
                          </div>
                        </div>
                        {groupBy.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-muted-foreground">Grouped By:</p>
                            <div className="flex flex-wrap gap-1.5">
                              {groupBy.map((gb, i) => (
                                <Badge key={i} variant="outline" className="text-xs">
                                  {gb.dimension === 'time' 
                                    ? `Time (${gb.time_granularity || 'day'})`
                                    : dimensionsRegistry.dimensions.find(d => d.id === gb.dimension)?.label || gb.dimension
                                  }
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Preview Chart Placeholder */}
                        <div className="mt-4 pt-4 border-t">
                          <div className="flex items-center justify-center h-32 bg-muted/30 rounded-lg">
                            {visualizationType === 'bar' && <BarChart3 className="h-16 w-16 text-muted-foreground/30" />}
                            {visualizationType === 'line' && <LineChart className="h-16 w-16 text-muted-foreground/30" />}
                            {visualizationType === 'area' && <AreaChart className="h-16 w-16 text-muted-foreground/30" />}
                            {visualizationType === 'pie' && <PieChart className="h-16 w-16 text-muted-foreground/30" />}
                            {visualizationType === 'table' && <Table2 className="h-16 w-16 text-muted-foreground/30" />}
                            {visualizationType === 'number' && <Hash className="h-16 w-16 text-muted-foreground/30" />}
                          </div>
                          <p className="text-[10px] text-center text-muted-foreground mt-2">
                            Live preview will appear after adding to page
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Info Banner */}
                  <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg text-sm">
                    <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <div className="text-blue-700 dark:text-blue-300">
                      <p className="font-medium">Data from Cometly</p>
                      <p className="text-xs mt-1 opacity-80">
                        All available metrics are pulled from your Cometly integration. 
                        Unavailable metrics require additional integrations.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer Actions */}
        <div className="flex-shrink-0 flex items-center justify-between pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => handleSave(false)}
              disabled={saving || selectedMetrics.length === 0}
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save Report
            </Button>
            <Button
              onClick={() => handleSave(true)}
              disabled={saving || selectedMetrics.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Add To Page
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReportBuilderModal;
